import { useEffect, useRef } from 'react'

import { useReducedMotion } from '../../ui/motion'
import type { LatticeHandle, LatticeMode } from './lattice'

/**
 * The host lattice, mounted.
 *
 * The component is a canvas and an effect; the engine is a separate chunk,
 * imported on mount, so the first bundle pays for a few hundred bytes rather
 * than for the drawing code. If the import fails — offline, an old cached
 * index — the page simply has no lattice, which it was designed to survive.
 *
 * Purely decorative: `aria-hidden`, no text, no focus, no pointer capture.
 * Where `matchMedia` is missing (jsdom) nothing is drawn at all, so tests
 * never touch a canvas context they cannot provide.
 */
export function Lattice({ mode, className = '' }: { mode: LatticeMode; className?: string }) {
  const ref = useRef<HTMLCanvasElement>(null)
  const reduced = useReducedMotion()

  useEffect(() => {
    const canvas = ref.current
    if (!canvas || typeof window.matchMedia !== 'function') return undefined
    let handle: LatticeHandle | null = null
    let cancelled = false
    void import('./lattice')
      .then(({ createLattice }) => {
        if (!cancelled) handle = createLattice(canvas, { mode, reduced })
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
      handle?.destroy()
    }
  }, [mode, reduced])

  return <canvas ref={ref} className={`lattice lattice-${mode}${className ? ` ${className}` : ''}`} aria-hidden="true" />
}
